import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { CSSProperties, FC } from 'react';
import { Header, Footer } from '../../index';
import {
  Badge,
  BulletList,
  Card,
  CardGrid,
  PageBody,
  PageHeading,
  PageSurface,
} from '../../../design-system/components';
import styles from './ModelExplorationPage.module.scss';

// Card media are panels of the lab's multi-scale pipeline figure
// (/slideshow/dbbs-pipeline.png, 3000×2000). Its panels are not an even grid,
// so each card frames a window given as fractions of the figure: left edge x,
// width w, top edge y. The slot is 2:1, so a window w wide shows
// w·(3/2)/2 of the figure's height from y down.
const FIGURE_ASPECT = 3000 / 2000;
// Media slot of the cards (2:1, lower than the default so the page fits on
// one screen)
const SLOT_ASPECT = 2;

interface PanelWindow {
  x: number;
  w: number;
  y: number;
}

const PANELS = {
  C: { x: 0.57, w: 0.43, y: 0 },
  D: { x: 0, w: 0.35, y: 0.52 },
  F: { x: 0.676, w: 0.324, y: 0.52 },
} satisfies Record<string, PanelWindow>;

type Panel = keyof typeof PANELS;

const panelStyle = ({ x, w, y }: PanelWindow): CSSProperties => {
  const visibleHeight = (w * FIGURE_ASPECT) / SLOT_ASPECT;
  return {
    backgroundSize: `${100 / w}% auto`,
    backgroundPosition: `${(x / (1 - w)) * 100}% ${(y / (1 - visibleHeight)) * 100}%`,
  };
};

const FigurePanel: FC<{ panel: Panel; label: string }> = ({ panel, label }) => (
  <div className={styles.figurePanel} role="img" aria-label={label} style={panelStyle(PANELS[panel])} />
);

interface ModelEntry {
  title: string;
  eyebrow: string;
  panel: Panel;
  panelLabel: string;
  description: string;
  features: string[];
  route?: string;
}

const models: ModelEntry[] = [
  {
    title: 'Microcircuits',
    eyebrow: 'Cellular resolution',
    panel: 'C',
    panelLabel: 'Reconstructed cerebellar cortex microcircuit',
    description:
      'Visualise the cerebellar neural microcircuit. Explore its architecture, replay simulations, and analyse cellular connectivity.',
    features: ['Single neuron morphology and dynamics', 'Cellular connectivity', 'Cell-type spiking activity'],
    route: '/microcircuits',
  },
  {
    title: 'Atlas modeling',
    eyebrow: 'Region scale',
    panel: 'D',
    panelLabel: 'Microcircuit fitted to the Declive region of the atlas',
    description:
      'Observe whole brain regions in 3D and how the cerebellar microcircuit adapts to the brain shape.',
    features: [
      'Region-specific cellular composition',
      'Connectivity from bent morphologies',
      'Spiking activity propagation in simulation',
    ],
    route: '/atlas',
  },
  {
    title: 'Digital twin',
    eyebrow: 'Whole brain',
    panel: 'F',
    panelLabel: 'Cerebellar nodes in a whole mouse brain model',
    description: 'Explore and simulate digital brain twin models based on clinical data.',
    features: [
      'Whole-brain structural and functional connectivity',
      'Cerebellar mean-field networks',
      'Node-specific virtual brains',
    ],
  },
];

const ModelExplorationPage: FC = () => {
  const navigate = useNavigate();

  const handleNavItemClick = (item: string) => {
    switch (item) {
      case 'Model Exploration':
        navigate('/model-exploration');
        break;
      case 'Tools':
        navigate('/tools');
        break;
      case 'Learning':
        navigate('/learning');
        break;
      case 'Contact':
        navigate('/contact');
        break;
      default:
        break;
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header pageTitle="Model exploration" onNavItemClick={handleNavItemClick} />

      <PageSurface grid fullHeight>
        <PageHeading
          compact
          title="Explore our brain models"
          lede="Three scales of cerebellar model, each with its own viewer. Start at cellular resolution and work outward to the whole brain."
        />
        <PageBody compact>
          <CardGrid columns={3}>
            {models.map((model) => {
              const available = model.route !== undefined;
              return (
                <Card
                  key={model.title}
                  eyebrow={model.eyebrow}
                  title={model.title}
                  media={<FigurePanel panel={model.panel} label={model.panelLabel} />}
                  mediaAspect={SLOT_ASPECT}
                  disabled={!available}
                  badge={!available ? <Badge>Coming soon</Badge> : undefined}
                  footer={
                    available ? (
                      <Button variant="contained" fullWidth onClick={() => navigate(model.route!)}>
                        Explore
                      </Button>
                    ) : (
                      <Button variant="contained" fullWidth disabled>
                        Coming soon
                      </Button>
                    )
                  }
                >
                  <p>{model.description}</p>
                  <BulletList items={model.features} />
                </Card>
              );
            })}
          </CardGrid>
        </PageBody>
      </PageSurface>

      <Footer />
    </div>
  );
};

export default ModelExplorationPage;
